/**
 * Project Form Modal Component
 * Create/Edit project form in a dialog
 */

import { useEffect, useState, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { CalendarIcon, Plus, Trash2, ChevronsUpDown, Check, FileText, Loader2, Package } from 'lucide-react';
import { format } from 'date-fns';
import type {
  Project,
  ProjectType,
  ProjectStatus,
  CreateProjectInput,
  UpdateProjectInput,
  ProjectSku,
  ScheduleDateStatus,
  ProjectTaskStatus,
  Quote,
  QuoteSku,
  InstalledBy,
  InitialProjectQuote,
} from '../types';
import { useMilestoneTemplates, useQuotes, useTakenQuoteNumbers } from '../hooks/useProjects';
import { fetchQuoteSkus } from '../api';
import { useTasksByProject } from '@/features/operations-tasks';

interface ProjectFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project?: Project;
  initialQuote?: InitialProjectQuote | null;
  onSubmit: (data: CreateProjectInput | UpdateProjectInput) => void;
  isSubmitting?: boolean;
}

type QuoteDisplay = Pick<Quote, 'id'> & Partial<Quote>;

interface FormData {
  quote_id?: string;
  quote_number: string;
  type: ProjectType;
  status: ProjectStatus;
  roadblock_reason?: string;
  account_id?: string;
  account_name: string;
  amount: number;
  currency: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  contact_phone_ext?: string;
  account_manager_name?: string;
  installed_by?: InstalledBy;
  notes?: string;
  skus: ProjectSku[];
  shipping_address?: string;
  shipping_date?: string;
  courier?: string;
  tracking_number?: string;
  notify_customer: boolean;
}

interface ScheduleDateEntry {
  id: string;
  date?: Date;
  time: string;
  status: ScheduleDateStatus;
}

const SCHEDULE_DATE_STATUSES: { value: ScheduleDateStatus; label: string }[] = [
  { value: 'proposed', label: 'Proposed' },
  { value: 'confirmed', label: 'Confirmed' },
];

interface SubtaskEntry {
  id: string;
  title: string;
  status: ProjectTaskStatus;
  position?: number;
}

interface TaskEntry {
  id: string;
  title: string;
  status: ProjectTaskStatus;
  deadline?: Date;
  subtasks: SubtaskEntry[];
}

const TASK_STATUSES: { value: ProjectTaskStatus; label: string }[] = [
  { value: 'todo', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'done', label: 'Done' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PROJECT_TYPES: { value: ProjectType; label: string }[] = [
  { value: 'installation', label: 'Installation' },
  { value: 'shipping', label: 'Shipping' },
  { value: 'calibration', label: 'Calibration' },
  { value: 'replacement', label: 'Replacement' }
];

const STATUS_OPTIONS_BY_TYPE: Record<string, { value: ProjectStatus; label: string }[]> = {
  replacement: [
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'sent_to_lab', label: 'Sent to Lab' },
    { value: 'shipped', label: 'Shipped' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'roadblock', label: 'Roadblock' },
  ],
  default: [
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'invoiced', label: 'Invoiced' },
    { value: 'roadblock', label: 'Roadblock' },
  ],
};

const CURRENCIES = ['USD', 'CAD', 'EUR', 'GBP'];

const COURIER_OPTIONS = [
  'Canada Post',
  'FedEx',
  'UPS',
  'DHL',
  'Purolator',
  'Planet Courier',
];

const INSTALLED_BY_OPTIONS: { value: InstalledBy; label: string }[] = [
  { value: 'FieldNation', label: 'FieldNation' },
  { value: 'LabMed', label: 'LabMed' },
  { value: 'ATEK', label: 'ATEK' },
  { value: 'Client', label: 'Client' },
];

function toProjectSkus(quoteSkus: QuoteSku[]): ProjectSku[] {
  return quoteSkus.map((sku) => ({
    id: crypto.randomUUID(),
    sku: sku.sku,
    name: sku.name || sku.description,
    quantity: sku.quantity,
    unitPrice: sku.unitPrice,
    description: sku.description,
  }));
}

export function ProjectFormModal({
  open,
  onOpenChange,
  project,
  initialQuote,
  onSubmit,
  isSubmitting,
}: ProjectFormModalProps) {
  const isEditing = !!project;
  const { data: milestoneTemplates } = useMilestoneTemplates();
  const [skus, setSkus] = useState<ProjectSku[]>([]);
  const [scheduledDates, setScheduledDates] = useState<ScheduleDateEntry[]>([]);
  const [tasks, setTasks] = useState<TaskEntry[]>([]);
  const [quoteSearch, setQuoteSearch] = useState('');
  const [quotePopoverOpen, setQuotePopoverOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<QuoteDisplay | null>(null);

  // Fetch quotes with search — only show actionable statuses (exclude preparation/sent/cancelled/invoiced/rejected/expired)
  const { data: quotesData, isLoading: quotesLoading } = useQuotes(
    {
      search: quoteSearch || undefined,
      pageLength: 50,
      sortKey: 'createdAt',
      sortDirection: -1,
      status_in: ['waiting-po', 'approved', 'confirmed', 'inprogress', 'invoice-ready', 'partially-invoiced'],
    },
    { enabled: open && !isEditing }
  );

  const { data: takenQuoteNumbers } = useTakenQuoteNumbers(open && !isEditing);

  const quotes = useMemo(() => {
    const all = quotesData?.quotes || [];
    const taken = new Set(takenQuoteNumbers || []);
    return all.filter((q) => !taken.has(q.quote_number));
  }, [quotesData, takenQuoteNumbers]);

  // Fetch existing tasks when editing a project (include subtasks for full editing support)
  const { data: existingTasksData, isLoading: isLoadingTasks } = useTasksByProject(
    isEditing ? project?.id : undefined,
    { pageLength: 100, includeSubtasks: true }
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      quote_id: '',
      type: 'installation',
      status: 'confirmed',
      currency: 'CAD',
      amount: 0,
      skus: [],
      notify_customer: false,
    },
  });

  const selectedType = watch('type');
  const statusOptions = STATUS_OPTIONS_BY_TYPE[selectedType] || STATUS_OPTIONS_BY_TYPE.default;

  // Reset status to 'confirmed' when type changes to/from replacement (user-initiated only).
  // We skip the first change after mount to avoid resetting when the edit form populates.
  const previousTypeRef = useRef<string | null>(null);
  useEffect(() => {
    if (previousTypeRef.current === null) {
      // First render — just record the initial type, don't reset
      previousTypeRef.current = selectedType;
      return;
    }
    const prevType = previousTypeRef.current;
    if (prevType !== selectedType) {
      const wasReplacement = prevType === 'replacement';
      const isReplacement = selectedType === 'replacement';
      if (wasReplacement !== isReplacement) {
        setValue('status', 'confirmed');
      }
      previousTypeRef.current = selectedType;
    }
  }, [selectedType, setValue]);

  // Reset form when project changes or modal opens/closes
  useEffect(() => {
    if (project) {
      reset({
        quote_number: project.quote_number || '',
        quote_id: project.quote_id || '',
        type: project.type,
        status: project.status,
        roadblock_reason: project.roadblock_reason || '',
        account_id: project.account_id,
        account_name: project.account_name || '',
        amount: project.amount || 0,
        currency: project.currency,
        contact_name: project.contact_name,
        contact_email: project.contact_email,
        contact_phone: project.contact_phone,
        contact_phone_ext: project.contact_phone_ext,
        account_manager_name: project.account_manager_name,
        installed_by: project.installed_by,
        notes: project.notes,
        skus: project.skus,
        shipping_address: project.shipping_address || '',
        shipping_date: project.shipping_date || '',
        courier: project.courier || '',
        tracking_number: project.tracking_number || '',
        notify_customer: project.notify_customer ?? true,
      });
      setSkus(project.skus || []);
      // Convert project.scheduled_dates to ScheduleDateEntry format
      const existingDates: ScheduleDateEntry[] = (project.scheduled_dates || []).map((d) => ({
        id: d.id,
        date: d.date ? new Date(d.date) : undefined,
        time: d.time || '',
        status: d.status,
      }));
      setScheduledDates(existingDates);
      setTasks([]); // Tasks are managed separately from the project entity
      setSelectedQuote(null);
      // Sync the type ref so the status-reset effect doesn't fire on mount
      previousTypeRef.current = project.type;
    } else {
      const initialProjectName = initialQuote?.project_name || initialQuote?.quote_title || '';
      const initialAmount = Number(initialQuote?.total) || 0;
      const initialCurrency = initialQuote?.currency || 'CAD';
      reset({
        type: 'installation',
        status: 'confirmed',
        roadblock_reason: '',
        currency: initialCurrency,
        amount: initialAmount,
        skus: [],
        quote_number: initialQuote?.quote_number || '',
        quote_id: initialQuote?.id || '',
        account_id: initialQuote?.account_id || undefined,
        account_name: initialQuote?.account_name || '',
        notes: initialProjectName,
        shipping_address: '',
        shipping_date: '',
        courier: '',
        tracking_number: '',
        notify_customer: false,
      });
      setSkus([]);
      setScheduledDates([]);
      setTasks([]);
      setSelectedQuote(initialQuote ? {
        id: initialQuote.id,
        quote_number: initialQuote.quote_number || '',
        account_id: initialQuote.account_id || undefined,
        account_name: initialQuote.account_name || '',
        project_name: initialProjectName,
        total: initialAmount,
        currency: initialCurrency,
        status: initialQuote.commercial_status,
      } as QuoteDisplay : null);
      setQuoteSearch('');
      previousTypeRef.current = 'installation';
    }
  }, [project, initialQuote, reset, open]);

  useEffect(() => {
    if (!open || isEditing || !initialQuote?.id) return;

    let cancelled = false;
    fetchQuoteSkus(initialQuote.id)
      .then((quoteSkus) => {
        if (!cancelled && quoteSkus.length > 0) {
          setSkus(toProjectSkus(quoteSkus));
        }
      })
      .catch((e) => {
        console.warn('Failed to fetch initial quote SKUs:', e);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isEditing, initialQuote?.id]);

  // Populate tasks when existing tasks are loaded (for editing)
  // Include `open` so tasks re-populate when the modal re-opens (Effect 1 clears them)
  useEffect(() => {
    if (open && isEditing && existingTasksData?.tasks) {
      const loadedTasks: TaskEntry[] = existingTasksData.tasks.map((t) => ({
        id: t.id,
        title: t.title,
        status: t.status as ProjectTaskStatus,
        deadline: t.due_date ? new Date(t.due_date) : undefined,
        subtasks: (t.subtasks || []).map((s) => ({
          id: s.id,
          title: s.title,
          status: s.status as ProjectTaskStatus,
          position: s.position,
        })),
      }));
      setTasks(loadedTasks);
    }
  }, [open, isEditing, existingTasksData]);

  // Handle quote selection - auto-populate form fields
  const handleQuoteSelect = async (quote: Quote) => {
    setSelectedQuote(quote);
    setQuotePopoverOpen(false);

    // Auto-populate form fields from quote
    setValue('quote_number', quote.quote_number);
    setValue('quote_id', quote.id);
    setValue('account_id', quote.account_id);
    setValue('account_name', quote.account_name || '');
    setValue('amount', quote.total);
    setValue('currency', quote.currency);

    // Prefill contact fields from contractual manager
    if (quote.contractual_manager_name) {
      setValue('contact_name', quote.contractual_manager_name);
    }
    if (quote.contractual_manager_email) {
      setValue('contact_email', quote.contractual_manager_email);
    }
    if (quote.contractual_manager_phone) {
      setValue('contact_phone', quote.contractual_manager_phone);
    }

    // Prefill account manager from sales manager
    if (quote.sales_manager_name) {
      setValue('account_manager_name', quote.sales_manager_name);
    }

    // Set project name from quote (use hubspot_dealname or project_name)
    if (quote.project_name) {
      setValue('notes', quote.project_name);
    }

    // Add estimated delivery date as a proposed date if available
    if (quote.estimated_delivery) {
      const newDate: ScheduleDateEntry = {
        id: crypto.randomUUID(),
        date: new Date(quote.estimated_delivery),
        time: '',
        status: 'proposed',
      };
      setScheduledDates((prev) => [...prev, newDate]);
    }

    // Fetch and populate SKUs from the quote
    try {
      const quoteSkus = await fetchQuoteSkus(quote.id);
      if (quoteSkus.length > 0) {
        setSkus(toProjectSkus(quoteSkus));
      }
    } catch (e) {
      // Silently fail - SKUs are optional
      console.warn('Failed to fetch quote SKUs:', e);
    }
  };

  const handleAddSku = () => {
    const newSku: ProjectSku = {
      id: crypto.randomUUID(),
      sku: '',
      name: '',
      quantity: 1,
    };
    setSkus([...skus, newSku]);
  };

  const handleRemoveSku = (id: string) => {
    setSkus(skus.filter((sku) => sku.id !== id));
  };

  const handleSkuChange = (id: string, field: keyof ProjectSku, value: string | number) => {
    setSkus(skus.map((sku) => (sku.id === id ? { ...sku, [field]: value } : sku)));
  };

  // Scheduled dates handlers
  const handleAddScheduledDate = () => {
    const newDate: ScheduleDateEntry = {
      id: crypto.randomUUID(),
      date: undefined,
      time: '',
      status: 'proposed',
    };
    setScheduledDates([...scheduledDates, newDate]);
  };

  const handleRemoveScheduledDate = (id: string) => {
    setScheduledDates(scheduledDates.filter((d) => d.id !== id));
  };

  const handleScheduledDateChange = (
    id: string,
    field: keyof ScheduleDateEntry,
    value: Date | string | ScheduleDateStatus | undefined
  ) => {
    setScheduledDates(scheduledDates.map((d) => (d.id === id ? { ...d, [field]: value } : d)));
  };

  // Task handlers
  const handleAddTask = () => {
    const newTask: TaskEntry = {
      id: crypto.randomUUID(),
      title: '',
      status: 'todo',
      subtasks: [],
    };
    setTasks([...tasks, newTask]);
  };

  const handleRemoveTask = (id: string) => {
    setTasks(tasks.filter((t) => t.id !== id));
  };

  const handleTaskChange = (
    id: string,
    field: keyof TaskEntry,
    value: string | ProjectTaskStatus | Date | undefined
  ) => {
    setTasks(tasks.map((t) => (t.id === id ? { ...t, [field]: value } : t)));
  };

  // Subtask handlers
  const handleAddSubtask = (taskId: string) => {
    setTasks(
      tasks.map((t) => {
        if (t.id !== taskId) return t;
        // Calculate max position from existing subtasks
        const maxPosition = Math.max(0, ...t.subtasks.map((s) => s.position ?? 0));
        const newSubtask: SubtaskEntry = {
          id: crypto.randomUUID(),
          title: '',
          status: 'todo',
          position: maxPosition + 1,
        };
        return { ...t, subtasks: [...t.subtasks, newSubtask] };
      })
    );
  };

  const handleRemoveSubtask = (taskId: string, subtaskId: string) => {
    setTasks(
      tasks.map((t) =>
        t.id === taskId
          ? { ...t, subtasks: t.subtasks.filter((s) => s.id !== subtaskId) }
          : t
      )
    );
  };

  const handleSubtaskChange = (
    taskId: string,
    subtaskId: string,
    field: keyof SubtaskEntry,
    value: string | ProjectTaskStatus
  ) => {
    setTasks(
      tasks.map((t) =>
        t.id === taskId
          ? {
              ...t,
              subtasks: t.subtasks.map((s) =>
                s.id === subtaskId ? { ...s, [field]: value } : s
              ),
            }
          : t
      )
    );
  };

  const onFormSubmit = (data: FormData) => {
    // Get milestone template for the selected type, fall back to universal template
    const template = milestoneTemplates?.find((t) => t.project_type === data.type)
      || milestoneTemplates?.find((t) => t.project_type === 'universal');
    const milestones = template?.milestones || [];

    // Convert scheduled dates to API format (filter out entries without a date)
    // Include ID for existing dates so backend can update them, new dates have no ID
    const existingDateIds = new Set(project?.scheduled_dates?.map((d) => d.id) || []);
    const formattedDates = scheduledDates
      .filter((d) => d.date)
      .map(({ id, date, time, status }) => ({
        id: existingDateIds.has(id) ? id : undefined, // Only include ID for existing dates
        date: date!.toISOString().split('T')[0],
        time: time || undefined,
        status,
      }));

    // Get the first confirmed date as the main scheduled_date for backwards compatibility
    const confirmedDate = scheduledDates.find((d) => d.status === 'confirmed' && d.date);
    const mainScheduledDate = confirmedDate?.date?.toISOString().split('T')[0];

    // Convert tasks to API format (filter out entries without a title)
    // Include ID for existing tasks so backend can update them, new tasks have no ID
    const existingTaskIds = new Set(existingTasksData?.tasks?.map((t) => t.id) || []);
    // Build a set of all existing subtask IDs (from all tasks)
    const existingSubtaskIds = new Set(
      existingTasksData?.tasks?.flatMap((t) => t.subtasks?.map((s) => s.id) || []) || []
    );
    const formattedTasks = tasks
      .filter((t) => t.title.trim())
      .map(({ id, title, status, deadline, subtasks }) => ({
        id: existingTaskIds.has(id) ? id : undefined, // Only include ID for existing tasks
        title,
        status,
        deadline: deadline ? deadline.toISOString().split('T')[0] : undefined,
        subtasks: subtasks
          .filter((s) => s.title.trim())
          .map(({ id: subtaskId, title: subtaskTitle, status: subtaskStatus, position: subtaskPosition }, index) => ({
            // Only include ID for existing subtasks so backend can update them
            id: existingSubtaskIds.has(subtaskId) ? subtaskId : undefined,
            title: subtaskTitle,
            status: subtaskStatus,
            // Preserve position for existing subtasks, use array index + 1 for new ones
            position: subtaskPosition ?? index + 1,
          })),
      }));

    const submitData: CreateProjectInput = {
      type: data.type,
      // When status is roadblock, include the reason; otherwise clear it (null) to remove any previous value
      roadblock_reason: data.status === 'roadblock' ? data.roadblock_reason : null,
      quote_id: data.quote_id || undefined,
      quote_number: data.quote_number,
      account_id: data.account_id,
      scheduled_date: mainScheduledDate || null,
      scheduled_dates: formattedDates,
      tasks: formattedTasks,
      amount: data.amount,
      currency: data.currency,
      contact_name: data.contact_name,
      contact_email: data.contact_email,
      contact_phone: data.contact_phone,
      contact_phone_ext: data.contact_phone_ext,
      account_manager_name: data.account_manager_name,
      installed_by: data.installed_by,
      notes: data.notes,
      shipping_address: data.shipping_address || undefined,
      shipping_date: data.shipping_date || null,
      courier: data.courier || undefined,
      tracking_number: data.tracking_number || undefined,
      skus: skus.map(({ id, ...rest }) => rest),
      milestones: isEditing ? undefined : milestones.map((m) => ({
        id: crypto.randomUUID(),
        key: m.key,
        label: m.displayName,
        displayName: m.displayName,
        sortOrder: m.sortOrder,
        status: 'pending' as const,
        completed: false,
      })),
      status: data.status,
      notify_customer: data.notify_customer,
    };

    onSubmit(submitData);
  };

  const formatQuoteDisplay = (quote: QuoteDisplay) => {
    const parts = [quote.quote_number || 'No quote #'];
    if (quote.account_name) parts.push(quote.account_name);
    return parts.join(' - ');
  };

  const formatQuoteSubtext = (quote: QuoteDisplay) => {
    const parts = [];
    if (quote.project_name) parts.push(quote.project_name);
    if (quote.total) parts.push(`$${quote.total.toLocaleString()} ${quote.currency}`);
    return parts.join(' | ');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Project' : 'New Project'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update project details below.'
              : initialQuote
                ? 'Create a supporting project for this quote.'
                : 'Create a new operations project. Select a quote to auto-fill details.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
          {/* Quote Selector (only for new projects) */}
          {!isEditing && (
            <div className="space-y-2">
              <Label>Select Quote</Label>
              <Popover open={quotePopoverOpen} onOpenChange={setQuotePopoverOpen} modal={false}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={quotePopoverOpen}
                    className="w-full justify-between"
                  >
                    {selectedQuote ? (
                      <span className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        {formatQuoteDisplay(selectedQuote)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Search for a quote...</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[500px] p-0" align="start" onWheel={(e) => e.stopPropagation()}>
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search by quote number, project name, or account..."
                      value={quoteSearch}
                      onValueChange={setQuoteSearch}
                    />
                    <CommandList>
                      {quotesLoading ? (
                        <div className="flex items-center justify-center py-6">
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          <span className="text-sm text-muted-foreground">Loading quotes...</span>
                        </div>
                      ) : quotes.length === 0 ? (
                        <CommandEmpty>No quotes found.</CommandEmpty>
                      ) : (
                        <CommandGroup>
                          {quotes.map((quote) => (
                            <CommandItem
                              key={quote.id}
                              value={quote.id}
                              onSelect={() => handleQuoteSelect(quote)}
                              className="flex flex-col items-start py-3"
                            >
                              <div className="flex items-center w-full">
                                <Check
                                  className={cn(
                                    'mr-2 h-4 w-4',
                                    selectedQuote?.id === quote.id ? 'opacity-100' : 'opacity-0'
                                  )}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium">{formatQuoteDisplay(quote)}</div>
                                  <div className="text-sm text-muted-foreground truncate">
                                    {formatQuoteSubtext(quote)}
                                  </div>
                                </div>
                                <span className={cn(
                                  'text-xs px-2 py-0.5 rounded',
                                  quote.status === 'approved' && 'bg-green-100 text-green-800',
                                  quote.status === 'confirmed' && 'bg-green-100 text-green-800',
                                  quote.status === 'sent' && 'bg-blue-100 text-blue-800',
                                  quote.status === 'inprogress' && 'bg-yellow-100 text-yellow-800',
                                  quote.status === 'invoice-ready' && 'bg-purple-100 text-purple-800'
                                )}>
                                  {quote.status}
                                </span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                Select a quote to auto-fill project details, or enter manually below.
              </p>
            </div>
          )}

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quote_number">Quote Number *</Label>
              <Input
                id="quote_number"
                placeholder="e.g., 7361-1"
                {...register('quote_number', { required: 'Quote number is required' })}
              />
              {errors.quote_number && (
                <p className="text-sm text-destructive">{errors.quote_number.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="account_name">Account Name *</Label>
              <Input
                id="account_name"
                placeholder="e.g., OPTILAB Estrie"
                {...register('account_name', { required: 'Account name is required' })}
              />
              {errors.account_name && (
                <p className="text-sm text-destructive">{errors.account_name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Project Type *</Label>
              <Select value={selectedType} onValueChange={(v) => setValue('type', v as ProjectType)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {PROJECT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status *</Label>
              <Select
                value={watch('status')}
                onValueChange={(v) => setValue('status', v as ProjectStatus)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Roadblock Reason - only shown when status is 'roadblock' */}
            {watch('status') === 'roadblock' && (
              <div className="space-y-2 col-span-2">
                <Label htmlFor="roadblock_reason">Roadblock Reason *</Label>
                <Textarea
                  id="roadblock_reason"
                  placeholder="Describe why this project is blocked..."
                  rows={2}
                  {...register('roadblock_reason', {
                    validate: (value, formValues) => {
                      if (formValues.status === 'roadblock' && !value?.trim()) {
                        return 'Roadblock reason is required';
                      }
                      return true;
                    },
                  })}
                />
                {errors.roadblock_reason && (
                  <p className="text-sm text-destructive">{errors.roadblock_reason.message}</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="installed_by">Installed By</Label>
              <Select
                value={watch('installed_by') || ''}
                onValueChange={(v) => setValue('installed_by', v as InstalledBy)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select installer" />
                </SelectTrigger>
                <SelectContent>
                  {INSTALLED_BY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="amount">Amount</Label>
              <div className="flex gap-2">
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  className="flex-1"
                  {...register('amount', { valueAsNumber: true })}
                />
                <Select
                  value={watch('currency')}
                  onValueChange={(v) => setValue('currency', v)}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((currency) => (
                      <SelectItem key={currency} value={currency}>
                        {currency}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h4 className="font-medium">Contact Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_name">Contact Name</Label>
                <Input id="contact_name" {...register('contact_name')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_email">Contact Email</Label>
                <Input id="contact_email" type="email" {...register('contact_email')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_phone">Contact Phone</Label>
                <div className="flex gap-2">
                  <Input id="contact_phone" {...register('contact_phone')} className="flex-1" />
                  <Input
                    id="contact_phone_ext"
                    placeholder="Ext."
                    {...register('contact_phone_ext')}
                    className="w-20"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="account_manager_name">Account Manager</Label>
                <Input id="account_manager_name" {...register('account_manager_name')} />
              </div>
            </div>
          </div>

          {/* Scheduled Dates */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Dates</h4>
              <Button type="button" variant="outline" size="sm" onClick={handleAddScheduledDate}>
                <CalendarIcon className="h-4 w-4 mr-1" />
                Add Date
              </Button>
            </div>
            {scheduledDates.length > 0 ? (
              <div className="space-y-2">
                {scheduledDates.map((entry) => (
                  <div key={entry.id} className="flex gap-2 items-center">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className={cn(
                            'w-[180px] justify-start text-left font-normal',
                            !entry.date && 'text-muted-foreground'
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {entry.date ? format(entry.date, 'yyyy-MM-dd') : 'yyyy-mm-dd'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={entry.date}
                          onSelect={(date) => handleScheduledDateChange(entry.id, 'date', date)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <Input
                      type="time"
                      placeholder="--:-- --"
                      value={entry.time}
                      onChange={(e) => handleScheduledDateChange(entry.id, 'time', e.target.value)}
                      className="w-[140px]"
                    />
                    <Select
                      value={entry.status}
                      onValueChange={(v) =>
                        handleScheduledDateChange(entry.id, 'status', v as ScheduleDateStatus)
                      }
                    >
                      <SelectTrigger className="w-[130px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SCHEDULE_DATE_STATUSES.map((status) => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      onClick={() => handleRemoveScheduledDate(entry.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No dates added yet.</p>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea id="notes" rows={3} {...register('notes')} />
          </div>

          {/* Shipping */}
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <Package className="h-4 w-4" />
              Shipping
            </h4>
            <div className="space-y-2">
              <Label htmlFor="shipping_address">Shipping Address</Label>
              <Textarea
                id="shipping_address"
                rows={2}
                placeholder="Enter shipping address..."
                {...register('shipping_address')}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Shipping Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !watch('shipping_date') && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {watch('shipping_date')
                        ? format(new Date(watch('shipping_date')!), 'yyyy-MM-dd')
                        : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={watch('shipping_date') ? new Date(watch('shipping_date')!) : undefined}
                      onSelect={(date) =>
                        setValue('shipping_date', date ? date.toISOString().split('T')[0] : '')
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label htmlFor="courier">Courier</Label>
                <Select
                  value={watch('courier') || ''}
                  onValueChange={(v) => setValue('courier', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select courier" />
                  </SelectTrigger>
                  <SelectContent>
                    {COURIER_OPTIONS.map((courier) => (
                      <SelectItem key={courier} value={courier}>
                        {courier}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tracking_number">Tracking Number</Label>
              <Input
                id="tracking_number"
                placeholder="Enter tracking number..."
                {...register('tracking_number')}
              />
            </div>
          </div>

          {/* Notifications */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="notify_customer"
              checked={watch('notify_customer')}
              onCheckedChange={(checked) => setValue('notify_customer', checked === true)}
            />
            <Label
              htmlFor="notify_customer"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Send email notifications to customer on milestone completion
            </Label>
          </div>

          {/* SKUs */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">SKUs</h4>
              <Button type="button" variant="outline" size="sm" onClick={handleAddSku}>
                <Plus className="h-4 w-4 mr-1" />
                Add SKU
              </Button>
            </div>
            {skus.length > 0 ? (
              <div className="space-y-2">
                {skus.map((sku) => (
                  <div key={sku.id} className="flex gap-2 items-center">
                    <Input
                      placeholder="SKU"
                      value={sku.sku}
                      onChange={(e) => handleSkuChange(sku.id, 'sku', e.target.value)}
                      className="w-32"
                    />
                    <Input
                      placeholder="Name"
                      value={sku.name}
                      onChange={(e) => handleSkuChange(sku.id, 'name', e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="Qty"
                      value={sku.quantity}
                      onChange={(e) =>
                        handleSkuChange(sku.id, 'quantity', parseInt(e.target.value) || 0)
                      }
                      className="w-20"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveSku(sku.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No SKUs added yet.</p>
            )}
          </div>

          {/* Tasks */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Tasks</h4>
              <Button type="button" variant="outline" size="sm" onClick={handleAddTask}>
                <Check className="h-4 w-4 mr-1" />
                Add Task
              </Button>
            </div>
            {tasks.length > 0 ? (
              <div className="space-y-3">
                {tasks.map((task) => (
                  <div
                    key={task.id}
                    className="border rounded-lg p-3 space-y-3"
                  >
                    {/* Task header row */}
                    <div className="flex gap-2 items-start">
                      <Input
                        placeholder="Task name"
                        value={task.title}
                        onChange={(e) => handleTaskChange(task.id, 'title', e.target.value)}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        onClick={() => handleRemoveTask(task.id)}
                        className="shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Task status and deadline row */}
                    <div className="flex gap-2 items-center flex-wrap">
                      <Select
                        value={task.status}
                        onValueChange={(v) =>
                          handleTaskChange(task.id, 'status', v as ProjectTaskStatus)
                        }
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TASK_STATUSES.map((status) => (
                            <SelectItem key={status.value} value={status.value}>
                              {status.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {/* Deadline picker */}
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              'w-[160px] justify-start text-left font-normal',
                              !task.deadline && 'text-muted-foreground'
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {task.deadline ? format(task.deadline, 'yyyy-MM-dd') : 'Deadline'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={task.deadline}
                            onSelect={(date) => handleTaskChange(task.id, 'deadline', date)}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>

                      {task.deadline && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleTaskChange(task.id, 'deadline', undefined)}
                          className="text-muted-foreground"
                        >
                          Clear
                        </Button>
                      )}

                      <div className="flex-1" />

                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleAddSubtask(task.id)}
                        className="text-muted-foreground"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Subtask
                      </Button>
                    </div>

                    {/* Subtasks */}
                    {task.subtasks.length > 0 && (
                      <div className="ml-4 pl-3 border-l-2 border-muted space-y-2">
                        {task.subtasks.map((subtask) => (
                          <div key={subtask.id} className="flex gap-2 items-center">
                            <Input
                              placeholder="Subtask name"
                              value={subtask.title}
                              onChange={(e) =>
                                handleSubtaskChange(task.id, subtask.id, 'title', e.target.value)
                              }
                              className="flex-1 h-8 text-sm"
                            />
                            <Select
                              value={subtask.status}
                              onValueChange={(v) =>
                                handleSubtaskChange(task.id, subtask.id, 'status', v as ProjectTaskStatus)
                              }
                            >
                              <SelectTrigger className="w-[120px] h-8 text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {TASK_STATUSES.map((status) => (
                                  <SelectItem key={status.value} value={status.value}>
                                    {status.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleRemoveSubtask(task.id, subtask.id)}
                            >
                              <Trash2 className="h-3 w-3 text-muted-foreground" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No tasks added yet.</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || (isEditing && isLoadingTasks)}>
              {isSubmitting ? 'Saving...' : (isEditing && isLoadingTasks) ? 'Loading...' : isEditing ? 'Save Changes' : 'Create Project'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
